import { getConsent, CONSENT_CHANGED_EVENT, type Consent } from './consent'

// ─── Configuration ──────────────────────────────────────────────────────────
// These IDs are public: they end up in the page source either way.

const GA4_ID = 'G-2J79N0WS6H'
const GOOGLE_ADS_ID = 'AW-18004283618'

export type AdsConversion = 'install' | 'signUp' | 'purchase'

// Labels from Google Ads → Goals → Conversions → (action) → Data sources →
// Manage → Use Google Tag Manager. While a label is empty that conversion only
// reaches GA4, where it can still be marked as a key event and imported into
// Google Ads.
const GOOGLE_ADS_LABELS: Record<AdsConversion, string> = {
  install: 'Xm3KCMrtoqkdEOKhjolD', // "Extension install"
  signUp: 'Hq2jCI_KqakdEOKhjolD', // "Register"
  purchase: 'EIAcCLz6oqkdEOKhjolD', // "Purchase"
}

// Microsoft Advertising UET tag ID. Empty means the tag never loads.
const MICROSOFT_UET_ID = ''

// Tags only load on the live site. Everywhere else (staging, localhost) events
// are written to the console instead, so QA sign-ups and test purchases never
// reach the ad accounts.
const PRODUCTION_HOST = 'cvcircuit.com'

// ─── Tag loading ────────────────────────────────────────────────────────────

const isProductionHost = () => window.location.hostname === PRODUCTION_HOST

let googleLoaded = false
const googleConfigured = { analytics: false, ads: false }
let uetLoaded = false

function gtagFn(): (...args: unknown[]) => void {
  const w = window as any
  w.dataLayer = w.dataLayer || []
  if (!w.gtag) {
    // gtag.js expects the arguments object itself, not an array.
    w.gtag = function gtag() { w.dataLayer.push(arguments) }
  }
  return w.gtag
}

function loadScript(src: string, onload?: () => void) {
  const s = document.createElement('script')
  s.async = true
  s.src = src
  if (onload) s.onload = onload
  document.head.appendChild(s)
}

function applyGoogle(consent: Consent) {
  if (!consent.analytics && !consent.ads) return
  const gtag = gtagFn()
  if (!googleLoaded) {
    gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    })
    gtag('js', new Date())
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${consent.analytics ? GA4_ID : GOOGLE_ADS_ID}`)
    googleLoaded = true
  }
  gtag('consent', 'update', {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.ads ? 'granted' : 'denied',
    ad_user_data: consent.ads ? 'granted' : 'denied',
    // Advertising consent covers measuring our own ads, not remarketing.
    ad_personalization: 'denied',
  })
  if (consent.analytics && !googleConfigured.analytics) {
    gtag('config', GA4_ID)
    googleConfigured.analytics = true
  }
  if (consent.ads && !googleConfigured.ads) {
    gtag('config', GOOGLE_ADS_ID)
    googleConfigured.ads = true
  }
}

function applyMicrosoft(consent: Consent) {
  if (!MICROSOFT_UET_ID || !consent.ads || uetLoaded) return
  const w = window as any
  w.uetq = w.uetq || []
  w.uetq.push('consent', 'default', { ad_storage: 'denied' })
  w.uetq.push('consent', 'update', { ad_storage: 'granted' })
  loadScript('https://bat.bing.com/bat.js', () => {
    const queued = w.uetq
    w.uetq = new w.UET({ ti: MICROSOFT_UET_ID, enableAutoSpaTracking: true, q: queued })
    w.uetq.push('pageLoad')
  })
  uetLoaded = true
}

function applyConsent(consent: Consent) {
  if (!isProductionHost()) return
  applyGoogle(consent)
  applyMicrosoft(consent)
}

// Withdrawing consent can't unload a tag that's already running, so the
// cookies it set are removed and the page reloads without it.
function deleteTrackingCookies() {
  const prefixes = ['_ga', '_gcl', '_uet']
  const host = window.location.hostname
  const domains = ['', host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`]
  for (const part of document.cookie.split(';')) {
    const name = part.split('=')[0].trim()
    if (!prefixes.some(p => name.startsWith(p))) continue
    for (const d of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ''}`
    }
  }
}

// ─── Events ─────────────────────────────────────────────────────────────────

type TrackOptions = {
  conversion?: AdsConversion
  value?: number
  currency?: string
  transactionId?: string
}

type QueuedEvent = { name: string; params: Record<string, unknown>; opts: TrackOptions }

// Events from before the visitor has chosen wait here; they're sent if the
// choice allows them and dropped otherwise.
const queued: QueuedEvent[] = []
const analyticsCallbacks: Array<() => void> = []

function send(consent: Consent, ev: QueuedEvent) {
  const { conversion, value, currency, transactionId } = ev.opts
  const label = conversion ? GOOGLE_ADS_LABELS[conversion] : ''
  const toGa4 = consent.analytics
  const toGoogleAds = consent.ads && !!label
  const toMicrosoft = consent.ads && !!MICROSOFT_UET_ID && !!conversion
  if (!toGa4 && !toGoogleAds && !toMicrosoft) return

  if (!isProductionHost()) {
    console.info('[CVCircuit track]', JSON.stringify({
      event: ev.name, ga4: toGa4, googleAds: toGoogleAds ? conversion : null, microsoft: toMicrosoft, params: ev.params,
    }))
    return
  }

  applyConsent(consent)
  const w = window as any
  try {
    if (toGa4) w.gtag('event', ev.name, ev.params)
    if (toGoogleAds) {
      w.gtag('event', 'conversion', {
        send_to: `${GOOGLE_ADS_ID}/${label}`,
        ...(value != null ? { value, currency } : {}),
        ...(transactionId ? { transaction_id: transactionId } : {}),
      })
    }
    if (toMicrosoft) {
      w.uetq.push('event', ev.name, value != null ? { revenue_value: value, currency } : {})
    }
  } catch { }
}

export function track(name: string, params: Record<string, unknown> = {}, opts: TrackOptions = {}) {
  if (typeof window === 'undefined') return
  const consent = getConsent()
  if (!consent) {
    queued.push({ name, params, opts })
    return
  }
  send(consent, { name, params, opts })
}

// Runs now if analytics is allowed, after the visitor allows it if they
// haven't chosen yet, and never if they've said no.
export function whenAnalyticsAllowed(cb: () => void) {
  const consent = getConsent()
  if (consent?.analytics) cb()
  else if (!consent) analyticsCallbacks.push(cb)
}

// ─── Where visitors came from ───────────────────────────────────────────────
// First and latest visit source, kept in this browser (with analytics consent
// only) and saved once on the account when it's created. Click IDs are kept
// only as "came from a Google / Microsoft ad"; referrers as a host name.

export type Touch = {
  source?: string
  medium?: string
  campaign?: string
  term?: string
  content?: string
  clickId?: 'google' | 'microsoft'
  referrer?: string
  landing: string
  at: string
}

const FIRST_TOUCH_KEY = 'cvcircuit:firstTouch'
const LAST_TOUCH_KEY = 'cvcircuit:lastTouch'

// Arrivals from these hosts are part of a visit already under way (returning
// from Stripe checkout, moving between our own sites), not a new source.
const IGNORED_REFERRERS = /(^|\.)cvcircuit\.com$|(^|\.)amplifyapp\.com$|(^|\.)stripe\.com$/

let pageTouch: Touch | null = null

function readTouch(): Touch | null {
  const url = new URL(window.location.href)
  const q = url.searchParams
  const pick = (key: string) => {
    const v = q.get(key)?.trim()
    return v ? v.slice(0, 100) : undefined
  }
  const touch: Touch = { landing: url.pathname.slice(0, 200), at: new Date().toISOString() }
  touch.source = pick('utm_source')
  touch.medium = pick('utm_medium')
  touch.campaign = pick('utm_campaign')
  touch.term = pick('utm_term')
  touch.content = pick('utm_content')
  if (q.get('gclid') || q.get('gbraid') || q.get('wbraid')) touch.clickId = 'google'
  else if (q.get('msclkid')) touch.clickId = 'microsoft'

  try {
    const ref = document.referrer ? new URL(document.referrer) : null
    if (ref && /^https?:$/.test(ref.protocol) && ref.hostname !== url.hostname && !IGNORED_REFERRERS.test(ref.hostname)) {
      touch.referrer = ref.hostname.slice(0, 100)
    }
  } catch { }

  if (!touch.source && !touch.clickId && !touch.referrer) {
    // The extension opens /welcome?from=install after a store install; that's
    // the store finding us, unless an earlier visit already said otherwise.
    if (q.get('from') !== 'install') return null
    touch.source = q.get('store') === 'edge' ? 'edge_addons' : 'chrome_web_store'
    touch.medium = 'store'
  }

  for (const k of Object.keys(touch) as Array<keyof Touch>) {
    if (touch[k] === undefined) delete touch[k]
  }
  return touch
}

// Read as soon as this module loads: React runs a page's effects before the
// root layout's, and a page may tidy its own URL (e.g. /welcome) in one.
if (typeof window !== 'undefined') pageTouch = readTouch()

function persistTouch() {
  if (!pageTouch || !getConsent()?.analytics) return
  try {
    if (!localStorage.getItem(FIRST_TOUCH_KEY)) localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(pageTouch))
    localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(pageTouch))
  } catch { }
  pageTouch = null
}

export function getAcquisition(): { first: Touch; last: Touch } | null {
  try {
    const first = localStorage.getItem(FIRST_TOUCH_KEY)
    const last = localStorage.getItem(LAST_TOUCH_KEY)
    if (!first) return null
    return { first: JSON.parse(first), last: JSON.parse(last || first) }
  } catch {
    return null
  }
}

// ─── Start-up ───────────────────────────────────────────────────────────────

let started = false

// Called once per full page load from the root layout.
export function startAnalytics() {
  if (started) return
  started = true

  const consent = getConsent()
  if (consent) {
    applyConsent(consent)
    persistTouch()
  }

  window.addEventListener(CONSENT_CHANGED_EVENT, (e: Event) => {
    const { previous, next } = (e as CustomEvent<{ previous: Consent | null; next: Consent }>).detail
    const withdrew = !!previous && ((previous.analytics && !next.analytics) || (previous.ads && !next.ads))
    if (withdrew) {
      deleteTrackingCookies()
      if (!next.analytics) {
        try {
          localStorage.removeItem(FIRST_TOUCH_KEY)
          localStorage.removeItem(LAST_TOUCH_KEY)
        } catch { }
      }
      window.location.reload()
      return
    }

    applyConsent(next)
    persistTouch()
    for (const ev of queued.splice(0)) send(next, ev)
    const callbacks = analyticsCallbacks.splice(0)
    if (next.analytics) callbacks.forEach(cb => { try { cb() } catch { } })
  })
}
